import { AppSettings } from "@/AppSettings";
import { ConfigurationStatus, Trial } from "@/api/dtos";
import { faExclamationCircle } from "@fortawesome/pro-duotone-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Alert, AlertTitle, Box, ListItemIcon, ListItemText, darken, lighten, styled } from "@mui/material";
import { ComponentType, FC, memo, useContext, useMemo } from "react";
import StatusList, { IStatusListCreateAdditional, IStatusListCreateConfigureLink, IStatusListCreateLabel, IStatusListCreateLink, IStatusListIsDisabled, IStatusListSearch, IStatusListStatsComponentProps, StatusListItemIcon, StatusListItemText, StatusListProps } from "./StatusList";
import { DefaultSortableFields, IStatusListSortable } from "./StatusListSort";
import DefaultStatComponents, { INoteLinkComponentProps, NoteLink, StatusListItemTooltip, StatusListStatsBox, getNoteCount } from './StatusListStats';
import Text, { ITextProps } from "@/components/forms/inputs/Text";
import Multiselect, { IMultiselectProps } from "@/components/forms/inputs/Multiselect";
import { Link as RouteLink } from "react-router-dom";
import ChosenThemeSizeContext from "@/contexts/styles/ChosenThemeSizeContext";


export interface ITrialStatusListAdditional {


    NoteLinkComponent?: ComponentType<INoteLinkComponentProps<Trial, ITrialStatusListAdditional>>
}

const TrialNoteLinkComponent = memo(({
    itemData,
    status,
    type
}: INoteLinkComponentProps<Trial, ITrialStatusListAdditional>) => {
    const value = getNoteCount(itemData, type, status);

    if (value === undefined) {
        return (
            <>
                &mdash;
            </>
        )
    }
    
    let baseLink = `/${itemData?.data?.oid}/notes-and-queries`;

    if (type && status) {
        baseLink += `?type=${type}&status=${status}`;
    }
    else if (type) {
        baseLink += `?type=${type}`;
    }
    else if (status) {
        baseLink += `?status=${status}`;
    }

    return (
        <NoteLink 
            //@ts-ignore
            component={RouteLink} 
            to={baseLink}
        >
            {value}
        </NoteLink>
    )
})

export interface ITrialStatusListProps extends StatusListProps<Trial, ITrialStatusListAdditional> {

}

const ProtocolBox = styled(Box)(({ theme }) => ({ 
    display: 'flex',
    alignItems: 'center',
    width: theme.spacing(15),
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    background: theme.palette.mode === 'dark' ? darken(theme.palette.primary.main,0.5) : lighten(theme.palette.primary.main, 0.5) 
}));

const createTrialStatusLabel: IStatusListCreateLabel<Trial, ITrialStatusListAdditional> = (itemData) => {
    return (
        <>
            <StatusListItemIcon>
                <FontAwesomeIcon fixedWidth size="lg" icon={AppSettings.icons.trial} />
            </StatusListItemIcon>
            <StatusListItemText 
                primary={itemData.data.name} 
            />
        </>
    )
};

const createTrialLink: IStatusListCreateLink<Trial, ITrialStatusListAdditional> = (itemData) => {
    return `/${itemData.data.oid}`
};

const createTrialConfigurationLink: IStatusListCreateConfigureLink<Trial, ITrialStatusListAdditional> = (itemData) => {
    return `/${itemData.data.oid}/configuration/trial`
};

const isTrialDisabled: IStatusListIsDisabled<Trial, ITrialStatusListAdditional> = (itemData) => {
    return !itemData.data.status || itemData.data.status === ConfigurationStatus.Disabled;
};

const TrialStatsComponent = ({
    itemData, 
    theme
}: IStatusListStatsComponentProps<Trial, ITrialStatusListAdditional>) => {
    return (
        <StatusListStatsBox>
            <DefaultStatComponents.DisabledIndicatorStats itemData={itemData} theme={theme} />
            <DefaultStatComponents.ReadOnlyIndicatorStats itemData={itemData} theme={theme} />
            <DefaultStatComponents.VerificationPriorityIndicatorStats itemData={itemData} theme={theme} />
            <StatusListItemTooltip
                placement="left"
                title={
                    <>
                        <strong>Protocol</strong>
                        <Box>
                            {itemData.data.protocol}
                        </Box>
                    </>
                }
            >
                <ProtocolBox>
                    {itemData.data.protocol}
                </ProtocolBox>
            </StatusListItemTooltip>
            <DefaultStatComponents.ParticipantStats itemData={itemData} theme={theme} />
            <DefaultStatComponents.FormStats itemData={itemData} theme={theme} />
            <DefaultStatComponents.NoteStats itemData={itemData} theme={theme} />
            <DefaultStatComponents.ReviewStats itemData={itemData} theme={theme} />
            <DefaultStatComponents.VerificationStats itemData={itemData} theme={theme} />
            <DefaultStatComponents.UpdateButton itemData={itemData} theme={theme} />
        </StatusListStatsBox>
    )
};


const SortableFields: IStatusListSortable<Trial, ITrialStatusListAdditional>[] = [
    {
        name: 'Name',
        sort: (a, b) => a.data.name! > b.data.name! ? 1 : -1
    },
    {
        name: 'Protocol',
        sort: (a, b) => a.data.protocol! > b.data.protocol! ? 1 : -1
    },
    DefaultSortableFields.priority,
    DefaultSortableFields.participants,
    DefaultSortableFields.crfCompletion,
    DefaultSortableFields.openQueries,
    DefaultSortableFields.reviewCompletion,
    DefaultSortableFields.verificationCompletion,
    DefaultSortableFields.status
]

const createTrialAdditional: IStatusListCreateAdditional<Trial, ITrialStatusListAdditional> = (itemData) => {
    return { NoteLinkComponent: TrialNoteLinkComponent }
}

const DefaultSearchData = { statuses: [ConfigurationStatus.Enabled, ConfigurationStatus.ReadOnly] };

const TrialStatusList: FC<ITrialStatusListProps> = ({
    ...statusListProps
}) => {
    const searchableFields: IStatusListSearch<Trial, ITrialStatusListAdditional>[] = useMemo(() => {
        const output: IStatusListSearch<Trial, ITrialStatusListAdditional, any, any>[] = [
            {
                key: 'name',
                component: Text,
                componentProps: {
                    margin: 'normal'
                } as ITextProps,
                label: 'Name',
                searchFn: (item, value) => value ? item.data.name?.toLowerCase()?.includes(value?.toLowerCase()) ?? false : false
            } as IStatusListSearch<Trial, ITrialStatusListAdditional, string>
        ]

        output.push({
            key: 'statuses',
            component: Multiselect,
            componentProps: {
                options: [
                    {
                        id: ConfigurationStatus.Disabled,
                        name: 'Disabled',
                        description: `The trial is disabled in the Source Data Verification system.`
                    },
                    {
                        id: ConfigurationStatus.ReadOnly,
                        name: 'Read Only',
                        description: `The trial is read only in the Source Data Verification system.`
                    },
                    {
                        id: ConfigurationStatus.Enabled,
                        name: 'Enabled',
                        description: `The trial is enabled in the Source Data Verification system.`
                    }
                ],
                FormControlProps: {
                    margin: 'normal'
                }
                
            } as IMultiselectProps,
            label: 'Statuses',
            searchFn: (item, value) => value && value.length > 0 ?
                item.data.status ?
                    value.includes(item.data.status) ?? false : 
                    false :
                true
        } as IStatusListSearch<Trial, ITrialStatusListAdditional, ConfigurationStatus[]>)

        return output;
    }, []);

    const themeSize = useContext(ChosenThemeSizeContext);

    const height = useMemo(() => {
        switch (themeSize) {
            case 'extra-small':
                return 25;
            case 'small':
                return 37;
            case 'medium':
                return 49;
            case 'large':
                return 61;
            case 'extra-large':
                return 73;
        }
    }, [themeSize])
    
    return (
        <StatusList 
            {...statusListProps}
            createLabel={createTrialStatusLabel}
            createLink={createTrialLink}
            createConfigureLink={createTrialConfigurationLink}
            isDisabled={isTrialDisabled}
            createAdditional={createTrialAdditional}
            StatsComponent={TrialStatsComponent}
            sortKey="trials-sort"
            sortableFields={SortableFields}
            noRecords={
                <Alert
                    severity="warning"
                >
                    <AlertTitle>No Trials</AlertTitle>
                    No trials were found.
                </Alert>
            }
            searchKey="trials-search"
            searchFields={searchableFields}
            fixedItemHeight={height}
            defaultSearchData={DefaultSearchData}
        />
    )
};

export default TrialStatusList;